import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    ElementRef,
    inject,
    input,
    NgZone,
    OnDestroy,
    OnInit,
    output,
    signal,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import '@genai/chatbot-ai-wc';

import { MatIconButton } from '@angular/material/button';
import { v4 } from 'uuid';
import { AiIaPdfViewerComponent, sphereChatbotThemeConfig } from '../../';
import { MatIcon } from '@angular/material/icon';
import { MtxSplit, MtxSplitPane } from '@ng-matero/extensions/split';
import { Observable, of } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
    AiFilterContextValues,
    CHATBOT_CONFIG_KEY,
    CHATBOT_MODE_INTELLIGENCE_BASIC,
    CHATBOT_MODE_MARKET_SCAN,
    ChatbotMode,
    ChatbotService,
    IASourceCategory
} from '../services';
import { rxResource } from '@angular/core/rxjs-interop';
import { AppConfigService } from '@sphere/platform';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';
import { IA_MARKET_SCAN, IA_PATH } from '../constants';
import { AuditService, ModelService, NewsVendorMetadataSchema } from '../api/sphere-ai-instant-analyst/generated';
import { IAFilterValues } from '../types';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { IaChatbotFilterPanelComponent } from './ia-chatbot-filter-panel/ia-chatbot-filter-panel.component';
import { IaMarketScanVendorCardComponent } from './ia-market-scan-vendor-card/ia-market-scan-vendor-card.component';

@Component({
    selector: 'ia-chatbot',
    templateUrl: './chatbot.component.html',
    styleUrl: './chatbot.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    encapsulation: ViewEncapsulation.ShadowDom,
    imports: [
        AiIaPdfViewerComponent,
        MatIcon,
        MatIconButton,
        MtxSplit,
        MtxSplitPane,
        MatProgressSpinner,
        MatTooltip,
        IaChatbotFilterPanelComponent,
        IaMarketScanVendorCardComponent
    ]
})
export class ChatbotComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly config = inject(AppConfigService);
    private readonly aiModelService = inject(ModelService);
    private readonly auditService = inject(AuditService);
    private readonly ngZone = inject(NgZone);
    chatbotService = inject(ChatbotService);
    http = inject(HttpClient);
    router = inject(Router);
    route = inject(ActivatedRoute);

    readonly chatbot = viewChild<ElementRef<ChatbotAiComponent>>('chatbot');
    mode = input<ChatbotMode>(CHATBOT_MODE_INTELLIGENCE_BASIC);
    isWidget = input<boolean>(false);
    initialConversationId = input<string>('');
    initialVendor = input<string>('');
    conversationIdChanged = output<string>();
    requestAccess = output<string>();

    conversationId = signal<string>('');

    isMarketScan = computed(() => {
        return this.mode() === CHATBOT_MODE_MARKET_SCAN;
    });

    private _auditUser = rxResource({
        stream: () => this.auditService.auditUserApiInstantAnalystAuditUserPost()
    });

    /* News Vendors */
    private _newsVendors = rxResource({
        stream: (): Observable<NewsVendorMetadataSchema[]> => {
            if (this.isMarketScan()) {
                return this.aiModelService.getSourcesForModelApiInstantAnalystModelModelCodeSourcesGet({ modelCode: IASourceCategory.Marketscan });
            } else {
                const data: NewsVendorMetadataSchema[] = [];
                return of(data);
            }
        }
    });
    public newsVendors = computed(() => this._newsVendors.value());
    public newsVendorsError = computed(() => this._newsVendors.error() as HttpErrorResponse);
    public areNewsVendorsLoading = computed(() => this._newsVendors.isLoading());

    currentPdfUrl = signal<string | null>(null);
    isPdfViewerLoading = signal(false);
    pdfError = signal(false);

    isPDF = signal<boolean>(false);

    currentPdfId = signal<string | null>('');
    currentPdfTitle = signal<string | null>(null);
    vendor = signal<string>('');
    context = signal<AiFilterContextValues | null>(null);
    private isRestoringFromInitialValues = signal<boolean>(false);

    isPdfViewerVisible = computed(() => {
        return !!this.currentPdfId();
    });

    areVendorCardsVisible = computed(() => {
        if (this.isMarketScan()) {
            if (!this.isRequestAccessClicked()) {
                return true;
            }
        }
        return false;
    });

    isRequestAccessClicked = signal(false);

    botConfig = computed(() => {
        return { product: this.mode() };
    });

    private previousVendor = signal<string | null>(null);

    themeConfig = sphereChatbotThemeConfig;

    readonly pdfLoader = rxResource({
        params: () => this.currentPdfId(),
        stream: ({ params: pdfId }) => {
            if (!pdfId) {
                return of(null);
            }
            this.isPdfViewerLoading.set(true);
            this.pdfError.set(false);
            return this.chatbotService.getPdfById(pdfId).pipe(
                switchMap(response => this.chatbotService.handlePdfResponse(response)),
                catchError(err => {
                    console.error('PDF load failed', err);
                    this.pdfError.set(true);
                    return of(null);
                }),
                finalize(() => this.isPdfViewerLoading.set(false))
            );
        }
    });

    constructor() {
        effect(() => {
            const url = this.pdfLoader.value();
            if (url) {
                this.currentPdfUrl.set(url);
            }
        });
        explicitEffect([this.conversationId], ([id]) => {
            if (id) {
                this.conversationIdChanged.emit(id);
            }
        });

        explicitEffect([this.vendor], ([currentVendor]) => {
            if (currentVendor) {
                this.setupConversationMonitoring();
            }
        });

        effect(() => {
            const initialVendor = this.initialVendor();
            if (initialVendor && this.isMarketScan() && this.isWidget() && !this.isRestoringFromInitialValues()) {
                if (!this.isRequestAccessClicked()) {
                    this.isRestoringFromInitialValues.set(true);
                    this.onRequestAccess(initialVendor);
                    setTimeout(() => this.isRestoringFromInitialValues.set(false), 100);
                }
            }
        });
    }

    ngOnInit(): void {
        if (this.initialConversationId()) {
            const chatId = this.initialConversationId();
            this.conversationId.set(chatId);
        }
        if (!this.isWidget()) {
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.getVendorPath(this.router.url));
        }
        this.ngZone.runOutsideAngular(() => {
            window.addEventListener('click', this.handleGlobalClick.bind(this));
        });
    }

    ngAfterViewInit(): void {
        if (this.initialConversationId()) {
            const chatId = this.initialConversationId();
            sessionStorage.setItem(
                CHATBOT_CONFIG_KEY,
                JSON.stringify({
                    chat_id: chatId
                })
            );
        } else {
            this.setSessionStorageConfig();
        }

        if (this.initialVendor() && this.isMarketScan() && this.isWidget()) {
            this.isRestoringFromInitialValues.set(true);
            this.onRequestAccess(this.initialVendor());
            setTimeout(() => this.isRestoringFromInitialValues.set(false), 100);
        } else {
            this.getVendorPath(this.router.url);
        }

        this.setupConversationMonitoring();
    }

    private setupConversationMonitoring(): void {
        const intervalId = setInterval(() => {
            if (this.chatbot()?.nativeElement?.addExternalEventPlugin) {
                try {
                    this.chatbot()?.nativeElement.addExternalEventPlugin({
                        conversation_change: () => {
                            this.ngZone.run(() => {
                                this.closeViewer();
                            });
                            if (this.isWidget()) {
                                this.checkSessionStorageForConversationId();
                            }
                        }
                    });
                    clearInterval(intervalId);
                } catch (error) {
                    console.warn('Failed to setup conversation monitoring:', error);
                }
            }
        }, 200);
    }

    private checkSessionStorageForConversationId(): void {
        try {
            const storedConfig = sessionStorage.getItem(CHATBOT_CONFIG_KEY);
            if (storedConfig) {
                const config = JSON.parse(storedConfig);
                const chatId = config.chat_id;

                if (chatId && chatId !== this.conversationId()) {
                    this.conversationId.set(chatId);
                }
            }
        } catch (error) {
            console.error('Error checking session storage:', error);
        }
    }

    private getVendorPath(currentUrl: string): void {
        const basePath = `/${IA_PATH}/${IA_MARKET_SCAN}/`;

        const [path] = currentUrl.split('#');

        const vendorMatch = path.startsWith(basePath) ? path.slice(basePath.length).split('/')[0] : null;

        this.isRequestAccessClicked.set(!!vendorMatch);

        if (this.previousVendor() !== vendorMatch) {
            this.previousVendor.set(vendorMatch);
            if (!this.isRestoringFromInitialValues()) {
                this.setSessionStorageConfig();
            }
            this.context.set(null);
            this.pdfError.set(false);
            this.currentPdfUrl.set(null);
            this.currentPdfId.set(null);
        }
    }

    ngOnDestroy(): void {
        try {
            window.removeEventListener('click', this.handleGlobalClick);
        } catch (e) {
            console.error(e);
        }
    }

    domain = computed(() => {
        return this.config.aiMarketScanUrl;
    });

    feedbackDomain = computed(() => {
        return this.config.aiMarketScanFeedbackUrl;
    });

    wsDomain = computed(() => {
        return this.config.aiMarketScanWebsocketUrl;
    });

    setSessionStorageConfig(): void {
        try {
            const chatId = v4();
            const newConfig = { chat_id: chatId };
            this.conversationId.set(chatId);
            sessionStorage.setItem(CHATBOT_CONFIG_KEY, JSON.stringify(newConfig));
        } catch (error) {
            console.error(error);
        }
    }
    onRequestAccess(source: string): void {
        this.vendor.set(source);
        this.requestAccess.emit(source);
        this.isRequestAccessClicked.set(true);
        if (!this.isWidget()) {
            this.router.navigate([IA_PATH, IA_MARKET_SCAN, source], {});
        }
    }

    filtersChanged(filters: IAFilterValues): void {
        const context = this.chatbotService.generateContext(filters, this.mode());
        this.context.set(context);
    }

    closeViewer(): void {
        this.currentPdfUrl.set(null);
        this.currentPdfId.set(null);
        this.currentPdfTitle.set(null);
    }

    retry(pdfId: string): void {
        this.currentPdfUrl.set(null);
        this.currentPdfId.set(null);
        this.currentPdfTitle.set(null);
        this.currentPdfId.set(pdfId);
    }

    handleGlobalClick = (event: Event) => {
        const path = event.composedPath && event.composedPath();
        if (path && Array.isArray(path)) {
            for (const element of path) {
                if (element instanceof HTMLAnchorElement) {
                    const href = element.getAttribute('href');

                    if (href && href.includes('citation')) {
                        event.preventDefault();
                        event.stopPropagation();

                        const match = href.match(/citation\/([^/]+)/);
                        if (match) {
                            const pdfId = match[1];
                            let title = element.getAttribute('title') || element.getAttribute('aria-label') || element.textContent?.trim() || null;
                            if (title) {
                                title = title.replace(/\[Page\s*\d*\]|\(\d{4}-\d{2}-\d{2}\)|[➪→←↑↓⬅➡⬆⬇]|\s+/g, ' ').trim();
                            }
                            console.debug('Found PDF ID in global handler:', pdfId);
                            this.currentPdfId.set(pdfId);
                            this.currentPdfTitle.set(title);
                            break;
                        }
                    }
                }
            }
        }
    };
}
